import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
  ViewContainerRef,
  inject,
  input,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import type { BillingPlanTier } from '@core/billing-types';
import { filterBoolean } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import type { FeatureDiscoveryFeatures } from '@core/feature-discovery-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { OnboardingVideo, OnboardingVideoId } from '@core/onboarding-lib-data';
import { IconComponent } from '@core/user-interface-legacy';
import type { PricingFeatureLoadedModel } from '@recruitee/billing-smart-ui';
import { PricingFeatureObservableModel } from '@recruitee/billing-smart-ui';
import { getProductCodenameFromSubscription } from '@recruitee/billing-util';

import { FeatureVideoAnalytics } from './infobox-video.analytics';
import { OnboardingVideosServiceBase } from '../../types/onboarding-videos-service.types';

@Component({
  selector: 'rt-infobox-video',
  templateUrl: './infobox-video.component.html',
  styleUrls: ['./infobox-video.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FeatureVideoAnalytics],
  imports: [InfoboxComponent, IconComponent, TranslatePipe],
})
export class InfoboxVideoComponent implements OnInit {
  private readonly onboardingVideosService = inject(OnboardingVideosServiceBase);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly i18nService = inject(I18nService);
  private readonly pricingFeatureObservableModel = inject(PricingFeatureObservableModel);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly analytics = inject(FeatureVideoAnalytics);
  private readonly store = inject(Store);

  public readonly heading = input<string>(undefined!);
  public readonly content = input<string>(undefined!);
  public readonly video = input<OnboardingVideoId>(undefined!);
  public readonly analyticsFeatureName = input<string>(undefined!);
  public readonly storageKey = input<string>(undefined!);
  public readonly featureDiscoveryKey = input<keyof FeatureDiscoveryFeatures>(undefined!);
  public readonly hideForBillingPlanTiers = input<Array<BillingPlanTier | 'trial'>>([]);
  public readonly hasNewBadge = input(false);
  public readonly marginBottom = input(true);

  public dismissClick = output<void>();

  @Input() public hidden: boolean;
  public onboardingVideo: OnboardingVideo;
  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.checkSubscription();
    this.getVideo();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
  public openOnboardingVideo() {
    this.analytics.trackOpenVideo(this.analyticsFeatureName());

    const dialogRef = this.onboardingVideosService.openVideoDialog(
      { ...this.onboardingVideo },
      this.viewContainerRef,
      false,
      {
        ctaButtonLabel: this.i18nService.translate('app.onboarding.walkthrough_tooltip.got_it'),
      },
    );

    dialogRef.afterClosed.pipe(takeUntil(this.ngOnDestroy$), filterBoolean).subscribe(() => {
      this.markFeatureUsed();
      this.onboardingVideosService.markAsWatched({
        id: this.onboardingVideo.id,
      });
    });
  }

  public handleDismissClick() {
    this.analytics.trackCloseInfobox(this.analyticsFeatureName());

    this.hidden = true;
    this.dismissClick.emit();
    this.markFeatureUsed();
    this.cdRef.detectChanges();
  }

  private getVideo() {
    this.onboardingVideosService
      .getVideo(this.video())
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(video => {
        this.onboardingVideo = video;
        this.hidden = this.hidden || video.watched;

        this.cdRef.detectChanges();
      });
  }

  private checkSubscription() {
    this.pricingFeatureObservableModel
      .pipe(
        filter(model => model.loaded),
        map((model: PricingFeatureLoadedModel) =>
          this.hideForBillingPlanTiers().some(billingPlanTier => {
            if (billingPlanTier === 'trial') return model.trial;
            return getProductCodenameFromSubscription(model.subscription) === billingPlanTier;
          }),
        ),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(shouldHide => {
        this.hidden = this.hidden || shouldHide;
        this.cdRef.detectChanges();
      });
  }

  private markFeatureUsed(): void {
    const featureDiscoveryKey = this.featureDiscoveryKey();
    if (featureDiscoveryKey) {
      this.store.dispatch(
        FeatureDiscoveryActions.update({ payload: { [featureDiscoveryKey]: true } }),
      );
    }
  }
}
